// src/design/designCenter.jsx
// Global Design Center modal - draggable, resizable, with tabbed interface.
// Mirrors chatcenter.jsx functionality but with a single panel body and tabs.

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { FaTimes, FaWindowMaximize, FaWindowRestore } from 'react-icons/fa';
import { useModalStore } from '../state/modalStore';
import { useProfileStore } from '../state/profileStore';
import DesignCenterWelcome from './DesignCenterWelcome';
import AdminDesignCenterReview from '../components/admin/AdminDesignCenterReview';
import DesignCenterWorkspace from './DesignCenterWorkspace';

const API_URL = process.env.REACT_APP_API_URL;

export default function DesignCenter() {
  const { designCenterOpen, closeDesignCenter } = useModalStore();
  const bringToFront = useModalStore((s) => s.bringToFront);
  const designCenterZ = useModalStore((s) => s.getZIndex('designCenter'));

  // Check if user has acknowledged the Design Center welcome
  const profile = useProfileStore((s) => s.profile);
  const updateProfile = useProfileStore((s) => s.updateProfile);
  const hasAcknowledged = profile?.u_design_center_acknowledged;
    // Admin-only visibility (THIS PHASE: u_id=109 + u_is_admin=true)
  const isDesignCenterAdmin = profile?.u_id === 109 && profile?.u_is_admin === true;
  const [showWelcome, setShowWelcome] = useState(false);

  const [showAdminReview, setShowAdminReview] = useState(false);
  const isAdminViewer = profile?.u_id === 109 && profile?.u_is_admin === true;

  // Active tab (must be declared before callbacks that reference it)
  const [activeTab, setActiveTab] = useState(null); // no default tab

  // Profession gate state
  const [professionChecked, setProfessionChecked] = useState(false);
  const [hasProfession, setHasProfession] = useState(false);
  const [professionName, setProfessionName] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');

  // Creation limit counter
  const [creationInfo, setCreationInfo] = useState(null); // { limit, activeCreationCount, tier }

  // Tab switch confirmation state
  const [hasActiveWork, setHasActiveWork] = useState(false);
  const [pendingTabId, setPendingTabId] = useState(null);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);

  // Close confirmation state
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Fetch creation limit status when Design Center opens,
  // and reset to home view every time a fresh session starts.
  useEffect(() => {
    if (!designCenterOpen) return;

    // Always start on the home / category-picker page when reopening
    setActiveTab(null);
    setHasActiveWork(false);

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscription/status`, { credentials: 'include' });
        const data = await res.json();
        if (data.ok) setCreationInfo({ limit: data.limit, activeCreationCount: data.activeCreationCount, tier: data.tier });
      } catch {}
    })();
  }, [designCenterOpen]);

  // Check profession on mount / when Design Center opens
  useEffect(() => {
    if (!designCenterOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/designcenter/check-profession`, { credentials: 'include' });
        const data = await res.json();
        if (cancelled) return;
        if (data.ok && data.hasProfession) {
          setHasProfession(true);
          setProfessionName(data.professionName || '');
        } else {
          setHasProfession(false);
          setProfessionName('');
        }
      } catch (err) {
        console.error('[designcenter] check-profession error:', err);
        if (!cancelled) setHasProfession(false);
      } finally {
        if (!cancelled) setProfessionChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [designCenterOpen]);

  // Submit profession from inline prompt
  const handleResolveProfession = useCallback(async () => {
    if (!industryInput.trim()) return;
    setResolving(true);
    setResolveError('');
    try {
      const res = await fetch(`${API_URL}/api/designcenter/resolve-profession`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ industryText: industryInput.trim() }),
      });
      const data = await res.json();
      if (data.ok && data.resolved) {
        setHasProfession(true);
        setProfessionName(data.professionName || '');
        // Hot-load into profile store so the profile page updates immediately
        updateProfile({ b_business_industry: industryInput.trim() });
      } else if (data.ok && !data.resolved) {
        setResolveError(data.message || 'Could not determine your profession. Please be more specific.');
      } else {
        setResolveError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('[designcenter] resolve-profession error:', err);
      setResolveError('Network error. Please try again.');
    } finally {
      setResolving(false);
    }
  }, [industryInput]);

  // Tab click handler — with confirmation if active work exists
  const handleTabClick = useCallback((tabId) => {
    if (tabId === activeTab) return;
    if (hasActiveWork) {
      setPendingTabId(tabId);
      setShowSwitchConfirm(true);
    } else {
      setActiveTab(tabId);
    }
  }, [activeTab, hasActiveWork]);

  const confirmTabSwitch = useCallback(() => {
    setShowSwitchConfirm(false);
    setHasActiveWork(false);
    if (pendingTabId !== null) {
      setActiveTab(pendingTabId);
      setPendingTabId(null);
    }
  }, [pendingTabId]);

  const cancelTabSwitch = useCallback(() => {
    setShowSwitchConfirm(false);
    setPendingTabId(null);
  }, []);

  // Close modal handler — warn if active work exists
  const handleCloseClick = useCallback(() => {
    if (hasActiveWork) {
      setShowCloseConfirm(true);
    } else {
      closeDesignCenter();
    }
  }, [hasActiveWork, closeDesignCenter]);

  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    setHasActiveWork(false);
    closeDesignCenter();
  }, [closeDesignCenter]);

  const cancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  // Show welcome modal when Design Center opens and user hasn't acknowledged.
  // Also force it open if user has no profession — they must go through the full
  // onboarding flow (profession toast on top → welcome underneath).
  useEffect(() => {
    if (designCenterOpen && !hasAcknowledged) {
      setShowWelcome(true);
    }
  }, [designCenterOpen, hasAcknowledged]);

  // If profession check completes and user has no profession, force welcome open
  // even if they previously acknowledged (they need to redo onboarding)
  useEffect(() => {
    if (professionChecked && !hasProfession && designCenterOpen) {
      setShowWelcome(true);
    }
  }, [professionChecked, hasProfession, designCenterOpen]);

  const handleWelcomeAcknowledge = () => {
    setShowWelcome(false);
  };

  const modalRef = useRef(null);
  const [windowMode, setWindowMode] = useState('norm');

  const [windowRect, setWindowRect] = useState(() => {
    const pad = 12;
    const w = Math.min(900, Math.floor(window.innerWidth * 0.85));
    const h = Math.floor(window.innerHeight * 0.85);
    return {
      width: w,
      height: h,
      x: Math.max(pad, Math.floor((window.innerWidth - w) / 2)),
      y: Math.max(pad, Math.floor((window.innerHeight - h) / 2)),
    };
  });

  const getMaxRect = useCallback(() => {
    const pad = 12;
    return {
      x: pad,
      y: pad,
      width: Math.max(600, window.innerWidth - pad * 2),
      height: Math.max(400, window.innerHeight - pad * 2),
    };
  }, []);

  const toggleMaximize = useCallback(() => {
    setWindowMode((prev) => (prev === 'max' ? 'norm' : 'max'));
  }, []);

  const onMinimizeClick = useCallback(() => {
    if (windowMode === 'max') setWindowMode('norm');
  }, [windowMode]);

  // Tab labels + folder keys (folder key maps to filesystem folder name)
  const tabs = [
    { id: 1, label: 'How Things Work', folderKey: 'how_things_work' },
    { id: 2, label: 'How-To & Tips', folderKey: 'how_to_tips' },
    { id: 3, label: 'Problems & Solutions', folderKey: 'problems_solutions' },
    { id: 4, label: 'Advice & Insights', folderKey: 'advice_insights' },
    { id: 5, label: 'Specials & Discounts', folderKey: 'specials_discounts' },
    { id: 6, label: 'Stories & Experiences', folderKey: 'stories_experiences' },
    { id: 7, label: 'Community Relations', folderKey: 'community_relations' },
    { id: 8, label: 'Humor & Personality', folderKey: 'humor_personality' },
  ];

  // Category cards — one per tab, click to enter that category.
  // Small SVG helper for circular icons
  const iconCircle = (bg, children) => (
    <div className={`w-12 h-12 rounded-full ${bg} flex items-center justify-center`}>
      {children}
    </div>
  );

  const goalCards = [
    {
      id: 1, tabId: 1,
      title: 'How Things Work',
      subtitle: 'Explain how things work in your industry. Educate your audience — people remember the experts who teach them.',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      icon: iconCircle('bg-green-200/50', (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#16a34a" strokeWidth="1.8">
          <circle cx="12" cy="12" r="3" />
          <path strokeLinecap="round" d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 010-4h.09A1.65 1.65 0 004.6 8a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V2a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H22a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
        </svg>
      )),
    },
    {
      id: 2, tabId: 2,
      title: 'How-To & Tips',
      subtitle: 'Step-by-step tips and how-to examples. Help your customers solve the things they face every day.',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      icon: iconCircle('bg-teal-200/50', (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#0d9488" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )),
    },
    {
      id: 3, tabId: 3,
      title: 'Problems & Solutions',
      subtitle: 'Show them problems they may face and how you solve them. Before-and-after examples build credibility fast.',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      icon: iconCircle('bg-blue-200/50', (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#2563eb" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
        </svg>
      )),
    },
    {
      id: 4, tabId: 4,
      title: 'Advice & Insights',
      subtitle: 'Your professional advice helps your audience make the right decisions when the time comes to choose.',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      icon: iconCircle('bg-indigo-200/50', (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#4f46e5" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
        </svg>
      )),
    },
    {
      id: 5, tabId: 5,
      title: 'Specials & Discounts',
      subtitle: 'Specials, offers, and exclusive coupons to attract new clients from your teammates\' channels. Keep it sane and profitable.',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      icon: iconCircle('bg-purple-200/50', (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#9333ea" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
        </svg>
      )),
    },
    {
      id: 6, tabId: 6,
      title: 'Stories & Experiences',
      subtitle: 'Relatable stories and real-life experiences — the posts people actually like, comment on, and share with their friends.',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      icon: iconCircle('bg-orange-200/50', (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#ea580c" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.21 1.671.337 2.555.337z" />
        </svg>
      )),
    },
    {
      id: 7, tabId: 7,
      title: 'Community Relations',
      subtitle: 'Community involvement and awareness. Show your audience you\'re part of the neighborhood, not just a business.',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      icon: iconCircle('bg-rose-200/50', (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#e11d48" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
        </svg>
      )),
    },
    {
      id: 8, tabId: 8,
      title: 'Humor & Personality',
      subtitle: 'Memes, behind-the-scenes, and relatable content. Let them know who you are — show the human side of your business.',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      icon: iconCircle('bg-amber-200/50', (
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="#d97706" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
        </svg>
      )),
    },
  ];

  if (!designCenterOpen) return null;

  return (
    <>
      {/* Welcome Modal - shows once per user */}
      {showWelcome && (
        <DesignCenterWelcome onAcknowledge={handleWelcomeAcknowledge} />
      )}

      {/* Profession Toast — floats on top of Welcome modal when user has no profession */}
      {professionChecked && !hasProfession && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-gray-200 w-[420px] max-w-[90vw] px-5 py-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#00aef7]/10 flex items-center justify-center shrink-0">
                <svg className="w-4 h-4 text-[#00aef7]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">One quick thing first...</h4>
                <p className="text-xs text-gray-500">What industry or profession are you in?</p>
              </div>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={industryInput}
                onChange={(e) => setIndustryInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !resolving) handleResolveProfession(); }}
                placeholder="e.g. Plumbing, Real Estate, Roofing..."
                className="eh-design-nodrag flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7]"
                disabled={resolving}
                autoFocus
              />
              <button
                type="button"
                onClick={handleResolveProfession}
                disabled={resolving || !industryInput.trim()}
                className={`eh-design-nodrag px-3 py-1.5 rounded-lg text-white text-xs font-medium transition ${
                  resolving || !industryInput.trim()
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-[#00aef7] hover:bg-[#0097d6]'
                }`}
              >
                {resolving ? (
                  <div className="flex items-center gap-1">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                    <span>Checking...</span>
                  </div>
                ) : 'Go'}
              </button>
            </div>

            {resolveError && (
              <p className="text-xs text-amber-600 mt-1.5">{resolveError}</p>
            )}
          </div>
        </div>
      )}

      <div
        style={{ zIndex: designCenterZ }}
      className="fixed inset-0 flex items-center justify-center pointer-events-none"
      aria-modal="true"
      role="dialog"
    >
      <Rnd
        cancel=".eh-design-nodrag"
        onMouseDownCapture={() => bringToFront('designCenter')}
        size={
          windowMode === 'max'
            ? { width: getMaxRect().width, height: getMaxRect().height }
            : { width: windowRect.width, height: windowRect.height }
        }
        position={
          windowMode === 'max'
            ? { x: getMaxRect().x, y: getMaxRect().y }
            : { x: windowRect.x, y: windowRect.y }
        }
        minWidth={600}
        minHeight={400}
        bounds="window"
        dragHandleClassName="eh-msg-header"
        disableDragging={windowMode === 'max'}
        enableResizing={
          windowMode === 'max'
            ? false
            : {
                bottomRight: true,
                bottomLeft: true,
                topRight: true,
                topLeft: true,
                left: true,
                right: true,
                bottom: true,
                top: true,
              }
        }
        onDragStop={(e, d) => {
          if (windowMode === 'max') return;
          setWindowRect((prev) => ({
            ...(prev || {}),
            x: d.x,
            y: d.y,
          }));
        }}
        onResizeStop={(e, direction, ref, delta, position) => {
          if (windowMode === 'max') return;
          setWindowRect({
            width: ref.offsetWidth,
            height: ref.offsetHeight,
            x: position.x,
            y: position.y,
          });
        }}
        className="rounded-2xl shadow-2xl overflow-hidden flex flex-col bg-white pointer-events-auto"
      >
        <div
          ref={modalRef}
          className="relative w-full h-full flex flex-col"
        >
          {/* Header - Brand blue background with white text */}
          <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-2 border-b border-[#00aef7] bg-[#00aef7] text-white cursor-move eh-msg-header">
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="text-base sm:text-lg font-semibold tracking-tight">
                eHandshake AI-powered Design Center
              </h2>
              {/* Creation limit counter DISABLED for pre-launch — unlimited for all users */}
              {/* {creationInfo && creationInfo.limit !== null && (
                <span className="text-xs font-medium bg-white/20 rounded-full px-2.5 py-0.5 whitespace-nowrap">
                  {creationInfo.activeCreationCount} / {creationInfo.limit} creation{creationInfo.limit === 1 ? '' : 's'}
                </span>
              )} */}
            </div>

            <div className="flex items-center gap-1">
              {isDesignCenterAdmin && (
                <button
                  type="button"
                  onClick={() => window.open('/admin/designcenter', '_blank', 'noopener,noreferrer')}
                  className="eh-design-nodrag inline-flex items-center justify-center rounded-md px-2 py-1 text-white hover:bg-white/15"
                  aria-label="Open Design Center Admin"
                  title="Open Design Center Admin"
                >
                  Admin
                </button>
              )}

              <button
                type="button"
                onClick={toggleMaximize}
                className="eh-design-nodrag w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
                title={windowMode === 'max' ? 'Restore' : 'Maximize'}
              >
                {windowMode === 'max' ? <FaWindowRestore size={12} /> : <FaWindowMaximize size={12} />}
              </button>
              <button
                onClick={handleCloseClick}
                className="eh-design-nodrag w-7 h-7 flex items-center justify-center rounded hover:bg-white/20 transition-colors"
                title="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
          </div>


          {/* Tabs Row - All tabs in brand blue, active has underline */}
          <div className="shrink-0 flex items-center gap-0 px-4 py-0 bg-white border-b border-gray-200 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab.id)}
                className={`eh-design-nodrag px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors text-[#00aef7] ${
                  activeTab === tab.id
                    ? 'border-[#00aef7]'
                    : 'border-transparent hover:border-[#00aef7]/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Body Content */}
          <div className="flex-1 min-h-0 overflow-auto px-4 py-1 bg-[#f8fafc] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            {showAdminReview ? (
              <AdminDesignCenterReview />
            ) : activeTab == null ? (
              <>
                {/* Content Header */}
                <div className="text-center mb-1 mt-1">
                  <h3 className="text-xl font-bold text-gray-800">Content ideas for {professionName || 'your business'}</h3>
                </div>

                {/* Category Cards — 4 x 2 grid, click to enter */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {goalCards.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => handleTabClick(card.tabId)}
                      className={`eh-design-nodrag text-center p-4 rounded-xl border-2 ${card.bgColor} ${card.borderColor} flex flex-col items-center gap-2 hover:shadow-md hover:scale-[1.02] transition-all`}
                    >
                      <div>{card.icon}</div>
                      <h4 className="text-sm font-semibold text-gray-800 leading-tight">{card.title}</h4>
                      <p className="text-xs text-gray-600 leading-snug">{card.subtitle}</p>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <DesignCenterWorkspace
                activeTabLabel={tabs.find((t) => t.id === activeTab)?.label}
                subcategoryKey={tabs.find((t) => t.id === activeTab)?.folderKey}
                onHasActiveWork={setHasActiveWork}
              />
            )}
          </div>

          {/* Tab Switch Confirmation Dialog */}
          {showSwitchConfirm && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 rounded-2xl">
              <div className="bg-white rounded-xl shadow-xl p-5 max-w-sm mx-4 text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">Switch Categories?</h4>
                <p className="text-xs text-gray-600 mb-4">
                  Switching categories will clear your current design. Are you sure?
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={cancelTabSwitch}
                    className="eh-design-nodrag px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Stay Here
                  </button>
                  <button
                    type="button"
                    onClick={confirmTabSwitch}
                    className="eh-design-nodrag px-4 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600"
                  >
                    Switch & Clear
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Close Confirmation Dialog */}
          {showCloseConfirm && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 rounded-2xl">
              <div className="bg-white rounded-xl shadow-xl p-5 max-w-sm mx-4 text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-3">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                  </svg>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">Close Design Center?</h4>
                <p className="text-xs text-gray-600 mb-4">
                  You have an active design in progress. Closing will discard your current work.
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={cancelClose}
                    className="eh-design-nodrag px-4 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Keep Editing
                  </button>
                  <button
                    type="button"
                    onClick={confirmClose}
                    className="eh-design-nodrag px-4 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600"
                  >
                    Close & Discard
                  </button>
                </div>
              </div>
            </div>
          )}


        </div>
      </Rnd>
    </div>
    </>
  );
}
